// Copyright 2025, Imprivata, Inc.  All rights reserved.

import { MfaEnrollmentBanner } from '@common/components/MfaEnrollmentBanner';
import { Alert, Button } from '@imprivata-cloud/design-system';
import { createHrefWithCurrentSearch } from '@utils/navigationSearch';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { isDismissed, markDismissed, SessionStorageKeys } from '@/common/utils/sessionStorage';
import { RootRoutes } from '@/index';
import { SelfServicePath } from '@/self-service/SelfServiceRoutes';
import DefaultLogo from '../../../assets/default_logo.svg';
import { useSession } from '../../../common/authorization/context/Session';
import {
  getDaysUntilExpiration,
  PASSWORD_EXPIRATION_WARNING_DAYS,
} from '../../../common/utils/passwordExpiration';
import { isAdminRole, productTags } from '../../constants';
import { useLandingPage } from '../../hooks/useLandingPage';
import { usePasswordExpirationInfo } from '../../hooks/usePasswordExpirationInfo';
import { ProductCard } from './cards/ProductCard';
import { UnpurchasedProductCard } from './cards/UnpurchasedProductCard';
import { getCardKey, getCardName, getCardTag, toLandingView } from './landingView';
import './PortalLanding.less';

const TRANSLATION_ROOT = 'portal.landing.password-expiration-banner';
const TRIAL_TRANSLATION_ROOT = 'portal.landing.trial';

const byName = <T,>(locale: string, nameOf: (item: T) => string) => {
  const { compare } = new Intl.Collator(locale, { sensitivity: 'accent' });
  return (a: T, b: T) => compare(nameOf(a), nameOf(b));
};

export const PortalLanding = () => {
  const { tenantId, userInfo } = useSession();
  const { data, isLoading } = useLandingPage();
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? i18n.language;
  const { data: passwordInfo } = usePasswordExpirationInfo();
  const [alertDismissed, setAlertDismissed] = useState(
    () =>
      sessionStorage.getItem(SessionStorageKeys.PASSWORD_EXPIRATION_DISMISSED_PORTAL) === 'true',
  );
  const [trialBannerDismissed, setTrialBannerDismissed] = useState(() =>
    isDismissed(SessionStorageKeys.TRIAL_ENDED_BANNER_DISMISSED),
  );
  const [searchParams] = useSearchParams();
  const daysUntilExpiration = getDaysUntilExpiration(passwordInfo?.passwordExpiresAt);
  const showPasswordAlert =
    daysUntilExpiration !== null &&
    daysUntilExpiration > 0 &&
    daysUntilExpiration <= PASSWORD_EXPIRATION_WARNING_DAYS &&
    !alertDismissed;

  // TODO: Remove this when local testing fixes the return of "string" for logos
  const resolvedLogoUrl =
    (data?.orgInfo?.logoUrl && data?.orgInfo?.logoUrl !== 'string' && data.orgInfo.logoUrl) ||
    undefined;

  const isAdmin = isAdminRole(userInfo?.roleType);
  const { grid, marketing, trialEnded } = useMemo(() => {
    const view = toLandingView(data, { isAdmin, now: Date.now() });
    return {
      grid: view.grid
        .filter(card => getCardTag(card) !== productTags.SSO || isAdmin)
        .sort(byName(locale, getCardName)),
      marketing: view.marketing
        .filter(entry => entry.product.productTag !== productTags.SSO)
        .sort(byName(locale, entry => entry.product.displayName)),
      trialEnded: view.trialEnded,
    };
  }, [data, isAdmin, locale]);
  const showTrialEndedBanner = trialEnded && !trialBannerDismissed;

  const passwordAlertActions = (
    <Button
      variant="default"
      size="sm"
      className="password-alert-link"
      onClick={() => {
        const url = createHrefWithCurrentSearch(
          `${RootRoutes.SELF_SERVICE}${SelfServicePath.SELF_SERVICE_IDENTITY_PASSWORD}`,
          searchParams,
        );
        // deepcode ignore OR: URL is built from constant route paths and current search params. The startsWith('/') guard ensures only a relative URL reaches window.open.
        /* c8 ignore next */
        window.open(url.startsWith('/') ? url : RootRoutes.SELF_SERVICE, '_blank', 'noopener,noreferrer');
      }}
    >
      {t(`${TRANSLATION_ROOT}.update-button`)}
    </Button>
  );

  return (
    <div className="landing">
      {showPasswordAlert && (
        <div className="password-expiration-alert">
          <Alert
            status="warning"
            title={t(`${TRANSLATION_ROOT}.title`, { count: daysUntilExpiration })}
            message={t(`${TRANSLATION_ROOT}.message`)}
            actions={passwordAlertActions}
            onClose={() => {
              setAlertDismissed(true);
              sessionStorage.setItem(
                SessionStorageKeys.PASSWORD_EXPIRATION_DISMISSED_PORTAL,
                'true',
              );
            }}
          />
        </div>
      )}
      <MfaEnrollmentBanner />
      {showTrialEndedBanner && (
        <div className="trial-ended-banner">
          <Alert
            status="info"
            title={t(`${TRIAL_TRANSLATION_ROOT}.ended-banner.title`)}
            message={t(`${TRIAL_TRANSLATION_ROOT}.ended-banner.message`)}
            onClose={() => {
              markDismissed(SessionStorageKeys.TRIAL_ENDED_BANNER_DISMISSED);
              setTrialBannerDismissed(true);
            }}
          />
        </div>
      )}
      <section className="landing-title-bar">
        <h1 className="h1">{t('portal.landing.title')}</h1>
        {!isLoading && (
          <img className="landing-logo" src={resolvedLogoUrl || DefaultLogo} alt="" />
        )}
      </section>
      {grid.length > 0 && (
        <section className="landing-panel">
          <div className="purchased-grid">
            {grid.map(card => (
              <ProductCard key={getCardKey(card)} card={card} tenantId={tenantId || ''} />
            ))}
          </div>
        </section>
      )}
      {marketing.length > 0 && (
        <section className="landing-panel">
          <h2 className="h2">{t('portal.landing.explore-heading')}</h2>
          <div className="learn-more-grid">
            {marketing.map(entry => (
              <UnpurchasedProductCard
                key={entry.product.productTag}
                product={entry.product}
                canStartTrial={entry.canStartTrial}
              />
            ))}
          </div>
        </section>
      )}
    </div>
  );
};
